import LFCBaseCharts from '_components/charts/base/LFCBaseCharts';
import {fmtYYYYMMDDHHmmss} from '_logics/LFCFormatUtil';
import {useParseWithSettings} from '_logics/LFCParseUtil';
import {colorPallet} from '_themes/color-pallet/ColorPallet';
import * as echarts from 'echarts';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {useTheme} from '@mui/material';

import {CloudDistanceDigGraphBaseData, SearchType} from '../types';

/**
 * 引数
 */
interface Props {
  title: string;
  source: CloudDistanceDigGraphBaseData;
  ldata: any;
  exportFilename?: string;
  zoomChartId?: string;
  zoomedChartId?: string;
  zoomCharts?: (zoomChartId?: string) => void;
  zoomChartsClose?: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;
  searchValue?: any;
  height?: string;
}

/**
 * LFCChartsCloudDistanceMaxVariationTrend
 * @param props
 * @returns
 */
const LFCChartsCloudDistanceMaxVariationTrend = React.memo(
  ({
    title,
    source,
    ldata,
    exportFilename,
    zoomChartId,
    zoomedChartId,
    zoomCharts,
    zoomChartsClose,
    searchValue,
    height
  }: Props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const {parseSize} = useParseWithSettings();
    const [legendSelected, setLegendSelected] = useState<any>({});

    const legendChange = (data: any) => {
      let graph: any = document.getElementById(zoomChartId !== undefined ? zoomChartId : 'graph');
      let graphZoom: any = document.getElementById('graphZoom');
      let echartsInstance: any;

      if (graphZoom === null) {
        echartsInstance = echarts.getInstanceByDom(graph.firstChild);
      } else {
        echartsInstance = echarts.getInstanceByDom(graphZoom.firstChild);
      }

      switch (data.name) {
        case '移動範囲':
          if (data.selected.移動範囲) {
            echartsInstance.dispatchAction({
              type: 'legendSelect',
              name: t('移動範囲')
            });
          } else {
            echartsInstance.dispatchAction({
              type: 'legendUnSelect',
              name: t('移動範囲')
            });
          }
          break;
        case '標準偏差':
          if (data.selected.標準偏差) {
            echartsInstance.dispatchAction({
              type: 'legendSelect',
              name: t('標準偏差')
            });
          } else {
            echartsInstance.dispatchAction({
              type: 'legendUnSelect',
              name: t('標準偏差')
            });
          }
          break;
        case '範囲':
          if (data.selected.範囲) {
            echartsInstance.dispatchAction({
              type: 'legendSelect',
              name: t('範囲')
            });
          } else {
            echartsInstance.dispatchAction({
              type: 'legendUnSelect',
              name: t('範囲')
            });
          }
          break;
        case 'Cp':
          if (data.selected.Cp) {
            echartsInstance.dispatchAction({
              type: 'legendSelect',
              name: 'Cp'
            });
          } else {
            echartsInstance.dispatchAction({
              type: 'legendUnSelect',
              name: 'Cp'
            });
          }
          break;
        case 'Cpk':
          if (data.selected.Cpk) {
            echartsInstance.dispatchAction({
              type: 'legendSelect',
              name: 'Cpk'
            });
          } else {
            echartsInstance.dispatchAction({
              type: 'legendUnSelect',
              name: 'Cpk'
            });
          }
          break;
      }
    };

    const searchKey =
      searchValue.searchType === SearchType.WORK
        ? 'idx'
        : searchValue.searchType === 1
          ? 'year_month_day_hour'
          : searchValue.searchType === 2
            ? 'year_month_day'
            : 'year_month';

    // 標準偏差S
    const standardDeviation = source?.xlist?.map((date: any) => {
      const matchedItems = ldata?.filter((item: any) => item[searchKey] === date);
      if (matchedItems.length === 0) return undefined;

      // 期間単位中の各データ数が10個未満の場合、該当日はプロットしない
      if (matchedItems.length < 10) return undefined;
      // マスター値やリミット値が複数ある場合、該当日はプロットしない
      const uniqueUpperLimits = new Set(
        matchedItems.map((item: {upper_limit: any}) => item.upper_limit)
      );
      const uniqueLowerLimits = new Set(
        matchedItems.map((item: {lower_limit: any}) => item.lower_limit)
      );
      if (uniqueUpperLimits.size > 1 || uniqueLowerLimits.size > 1) return undefined;

      if (matchedItems.length === 1) return matchedItems[0].upper_distance;

      const mean =
        matchedItems.reduce(
          (acc: any, item: {upper_distance: any}) => acc + item.upper_distance,
          0
        ) / matchedItems.length;
      const variance =
        matchedItems.reduce(
          (acc: number, item: {upper_distance: number}) =>
            acc + Math.pow(item.upper_distance - mean, 2),
          0
        ) / matchedItems.length;
      return Math.sqrt(variance);
    });

    const cpValue = source?.xlist?.map((date: any) => {
      const matchedItems = ldata?.filter((item: any) => item[searchKey] === date);
      if (matchedItems.length === 0) return undefined;

      // 期間単位中の各データ数が10個未満の場合、該当日はプロットしない
      if (matchedItems.length < 10) return undefined;
      // マスター値やリミット値が複数ある場合、該当日はプロットしない
      const uniqueUpperLimits = new Set(
        matchedItems.map((item: {upper_limit: any}) => item.upper_limit)
      );
      const uniqueLowerLimits = new Set(
        matchedItems.map((item: {lower_limit: any}) => item.lower_limit)
      );
      if (uniqueUpperLimits.size > 1 || uniqueLowerLimits.size > 1) return undefined;

      const upperLimit = matchedItems[0].upper_limit;
      const lowerLimit = matchedItems[0].lower_limit;

      if (matchedItems.length === 1) {
        const standardDeviation = matchedItems[0].upper_distance;
        return (upperLimit - lowerLimit) / (6 * standardDeviation);
      }

      const mean =
        matchedItems.reduce(
          (acc: any, item: {upper_distance: any}) => acc + item.upper_distance,
          0
        ) / matchedItems.length;
      const variance =
        matchedItems.reduce(
          (acc: number, item: {upper_distance: number}) =>
            acc + Math.pow(item.upper_distance - mean, 2),
          0
        ) / matchedItems.length;
      const standardDeviation = Math.sqrt(variance);

      return (upperLimit - lowerLimit) / (6 * standardDeviation);
    });

    const cpkValue = source?.xlist?.map((date: any) => {
      const matchedItems = ldata?.filter((item: any) => item[searchKey] === date);
      if (matchedItems.length === 0) return undefined;

      // 期間単位中の各データ数が10個未満の場合、該当日はプロットしない
      if (matchedItems.length < 10) return undefined;
      // マスター値やリミット値が複数ある場合、該当日はプロットしない
      const uniqueUpperLimits = new Set(
        matchedItems.map((item: {upper_limit: any}) => item.upper_limit)
      );
      const uniqueLowerLimits = new Set(
        matchedItems.map((item: {lower_limit: any}) => item.lower_limit)
      );
      if (uniqueUpperLimits.size > 1 || uniqueLowerLimits.size > 1) return undefined;

      const upperLimit = matchedItems[0].upper_limit;
      const lowerLimit = matchedItems[0].lower_limit;

      if (matchedItems.length === 1) {
        const mean = matchedItems[0].upper_distance;
        const standardDeviation = mean; // 1つのデータポイントでは標準偏差がその値と同じ
        const value1 = Math.abs(upperLimit - mean) / (3 * standardDeviation);
        const value2 = Math.abs(mean - lowerLimit) / (3 * standardDeviation);
        return Math.min(value1, value2);
      }

      const mean =
        matchedItems.reduce(
          (acc: any, item: {upper_distance: any}) => acc + item.upper_distance,
          0
        ) / matchedItems.length;
      const variance =
        matchedItems.reduce(
          (acc: number, item: {upper_distance: number}) =>
            acc + Math.pow(item.upper_distance - mean, 2),
          0
        ) / matchedItems.length;
      const standardDeviation = Math.sqrt(variance);

      const value1 = Math.abs(upperLimit - mean) / (3 * standardDeviation);
      const value2 = Math.abs(mean - lowerLimit) / (3 * standardDeviation);

      return Math.min(value1, value2);
    });

    // 範囲R / 移動範囲Rs
    const rangeValue =
      searchValue.searchType !== SearchType.WORK
        ? source?.xlist?.map((date: any) => {
            const matchedItems = ldata?.filter((item: any) => item[searchKey] === date);

            if (matchedItems.length === 0) return undefined;

            const maxUpperDistance = Math.max(
              ...matchedItems.map((item: {upper_distance: any}) => item.upper_distance)
            );
            const minUpperDistance = Math.min(
              ...matchedItems.map((item: {upper_distance: any}) => item.upper_distance)
            );

            return maxUpperDistance - minUpperDistance;
          })
        : ldata.map((item: any, i: number) => {
            if (i === 0) return undefined;
            return Math.abs(item.upper_distance - ldata[i - 1].upper_distance);
          });

    const [exportFields, setExportFields] = useState({});
    const [exportData, setExportData] = useState<any>([]);

    useEffect(() => {
      if (searchValue.searchType === SearchType.WORK) {
        //検査ごと
        setLegendSelected({
          移動範囲: true
        });

        setExportFields({
          シリアルナンバー: t('シリアルナンバー'),
          検査日時: t('検査日時'),
          検査部位: t('検査部位'),
          移動範囲: t('移動範囲')
        });

        const _exportData = source.xlist
          .map((xval: string, i: number) => {
            if (!rangeValue[i]) return null;

            return {
              シリアルナンバー: source.detail[i].serial || source.detail[i].uuid,
              検査日時: fmtYYYYMMDDHHmmss(source.detail[i].lq_time),
              検査部位: source.map,
              移動範囲: parseSize(rangeValue[i])
            };
          })
          .filter((item: any) => item !== null); // nullの行を除外

        setExportData(_exportData);
      } else {
        setLegendSelected({
          標準偏差: true,
          範囲: false,
          Cp: false,
          Cpk: false
        });

        setExportFields({
          検査日時: t('検査日時'),
          検査部位: t('検査部位'),
          標準偏差: t('標準偏差'),
          範囲_最大最小差: t('範囲（最大最小差）'),
          Cp: t('Cp'),
          Cpk: t('Cpk')
        });

        const _exportData = source.xlist
          .map((xval: string, i: number) => {
            if (!standardDeviation[i]) return null;

            return {
              検査日時: fmtYYYYMMDDHHmmss(xval),
              検査部位: source.map,
              標準偏差: parseSize(standardDeviation[i]),
              範囲_最大最小差: parseSize(rangeValue[i]),
              Cp: parseSize(cpValue[i] || 0),
              Cpk: parseSize(cpkValue[i] || 0)
            };
          })
          .filter((item: any) => item !== null); // nullの行を除外

        setExportData(_exportData);
      }
    }, [source]);

    return (
      <LFCBaseCharts
        option={{
          title: {
            text: title
          },
          grid: {
            top: 40,
            left: 50,
            right: 5,
            bottom: 60
          },
          legend: {
            type: 'scroll',
            bottom: 0,
            data:
              searchValue.searchType === SearchType.WORK
                ? [t('移動範囲')]
                : [t('標準偏差'), t('範囲'), 'Cp', 'Cpk'],
            formatter: (prm: any) => {
              return t(prm);
            },
            selected: legendSelected
          },
          tooltip: {
            trigger: 'axis',
            formatter: (prm: any) => {
              let tmp: any;
              tmp = t('計測日時') + ':' + prm[0].axisValue + '<br/>';
              prm.forEach((a: any) => {
                if (a.value !== undefined) {
                  tmp += a.marker + t(a.seriesName) + ':' + parseSize(a.value) + '<br/>';
                } else {
                  tmp += a.marker + t(a.seriesName) + ':' + t('データなし') + '<br/>';
                }
              });
              return tmp;
            }
          },
          dataZoom: [
            {
              type: 'inside'
            }
          ],
          xAxis: {
            type: 'category',
            data: source.xlist,
            axisLabel: {
              rotate: '15',
              fontSize: 10
            }
          },
          yAxis: [
            {
              type: 'value'
            }
          ],
          series:
            searchValue.searchType === SearchType.WORK
              ? [
                  {
                    name: t('移動範囲'),
                    data: rangeValue,
                    stack: false,
                    type: 'line',
                    showSymbol: true,
                    showAllSymbol: true,
                    color: theme.palette.primary.main
                  }
                ]
              : [
                  {
                    name: t('標準偏差'),
                    data: standardDeviation,
                    stack: false,
                    type: 'line',
                    showSymbol: true,
                    showAllSymbol: true,
                    color: theme.palette.primary.main
                  },
                  {
                    name: t('範囲'),
                    data: rangeValue,
                    stack: false,
                    type: 'line',
                    showSymbol: true,
                    showAllSymbol: true,
                    color: colorPallet[1]
                  },
                  {
                    name: 'Cp',
                    data: cpValue,
                    stack: false,
                    type: 'line',
                    showSymbol: true,
                    showAllSymbol: true,
                    color: colorPallet[2]
                  },
                  {
                    name: 'Cpk',
                    data: cpkValue,
                    stack: false,
                    type: 'line',
                    showSymbol: true,
                    showAllSymbol: true,
                    color: colorPallet[3]
                  }
                ]
        }}
        exportData={exportData}
        exportFields={exportFields}
        exportFilename={exportFilename}
        height={height !== undefined ? height : '300px'}
        onEvents={{
          legendselectchanged: (params: any) => {
            legendChange(params);
          }
        }}
        zoomCharts={zoomCharts ? zoomCharts : undefined}
        zoomChartsClose={zoomChartsClose ? zoomChartsClose : undefined}
        zoomChartId={zoomChartId ? zoomChartId : undefined}
        zoomStatus={zoomChartId === zoomedChartId}
      />
    );
  }
);

LFCChartsCloudDistanceMaxVariationTrend.displayName = 'LFCChartsCloudDistanceMaxVariationTrend';

export default LFCChartsCloudDistanceMaxVariationTrend;
